import React from "react";
import { Bone, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Placeholder "Pronto un nuevo Hueso disponible".
 * Mantiene el layout del CouponCard real: panel mostaza a la izquierda con eyebrow
 * + título + botón bloqueado, y panel NEGRO a la derecha con un ejemplo de recompensa
 * (10% OFF, 2x1, COMBO, FREE) para insinuar qué tipo de premio podría llegar.
 *
 * Diseño:
 *  - Compacto (minHeight ~190px en sm+).
 *  - Bordes redondeados (rounded-xl) + border negro.
 *  - hover-lift (translate -3px,-3px) en hover y :active (click).
 */
const PLACEHOLDER_REWARDS = ["10% OFF", "2X1", "COMBO", "FREE"];

export default function CouponPlaceholderCard({ index = 0 }) {
  const { t } = useTranslation();
  const titleHtml = { __html: t("huesos.placeholderTitle") };
  const exampleReward = PLACEHOLDER_REWARDS[index % PLACEHOLDER_REWARDS.length];

  return (
    <div
      className="group relative bg-[#FEBA01] text-black overflow-hidden rounded-xl hover-lift cursor-default select-none outline-none focus:outline-none focus-visible:outline-none"
      data-testid={`hueso-placeholder-${index}`}
      role="article"
      aria-label={t("huesos.placeholderBadge")}
    >
      {/* Badge "Próximamente" arriba a la derecha del lado mostaza */}
      <div
        className="absolute top-2.5 left-2.5 z-20 bg-black text-[#FEBA01] font-mono text-[9px] uppercase tracking-widest px-2 py-1 border border-[#FEBA01] rounded-sm flex items-center gap-1"
        aria-hidden="true"
      >
        <Bone size={10} strokeWidth={2.4} /> {t("huesos.placeholderBadge")}
      </div>

      <div className="relative grid grid-cols-[1fr_auto] z-10" style={{ minHeight: 190 }}>
        {/* Lado mostaza — eyebrow + título + botón bloqueado */}
        <div className="p-5 pt-9 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-black/70">
              <Bone size={14} strokeWidth={2.2} /> {t("huesos.huesoKiltro")}
            </div>
            <h3
              className="mt-2 font-display text-xl sm:text-2xl leading-[1.05] uppercase tracking-tight"
              dangerouslySetInnerHTML={titleHtml}
            />
          </div>
          <div className="mt-4">
            <button
              type="button"
              disabled
              className="inline-flex items-center gap-1.5 bg-black text-[#FEBA01] font-display uppercase tracking-wider px-3 py-1.5 text-xs rounded-sm cursor-not-allowed"
              aria-disabled="true"
              data-testid={`hueso-placeholder-${index}-locked-btn`}
            >
              <Lock size={12} /> {t("huesos.placeholderLocked")}
            </button>
          </div>
        </div>

        {/* Lado negro — ejemplo de recompensa */}
        <div className="bg-black text-[#FEBA01] flex flex-col items-center justify-center px-5 sm:px-7 relative">
          <div className="font-display text-2xl sm:text-3xl uppercase tracking-tighter leading-none text-center opacity-60">
            {exampleReward}
          </div>
          <div className="mt-1.5 font-mono text-[9px] uppercase tracking-widest opacity-50">
            {t("huesos.reward")}
          </div>
        </div>
      </div>
    </div>
  );
}
